import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import { getUploadUrl, toUploadPath } from '../utils/uploadUrl';
import TextEditor from '../components/TextEditor';

function AdminTestsProcedures() {
  const [testsProcedures, setTestsProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTestProcedure, setEditingTestProcedure] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    first_letter: '',
    description: '',
    overview: '',
    why_its_done: '',
    how_you_prepare: '',
    what_you_can_expect: '',
    results: '',
    risks: '',
    content: '',
    image: '',
    status: 'active'
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchTestsProcedures();
  }, [navigate, search, letterFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, letterFilter]);

  const fetchTestsProcedures = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (letterFilter) params.append('letter', letterFilter);
      params.append('include_inactive', 'true'); // Include inactive for admin
      params.append('page', String(currentPage));

      const response = await axios.get(`/api/tests-procedures?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setTestsProcedures(response.data.tests_procedures);
      setPagination(response.data.pagination);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching tests & procedures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update only the image field, preserving all other form data
      const imageUrl = toUploadPath(response.data.filename || response.data.path || response.data.imageUrl);
      setFormData(prevFormData => ({ ...prevFormData, image: imageUrl }));
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      alias: '',
      first_letter: '',
      description: '',
      overview: '',
      why_its_done: '',
      how_you_prepare: '',
      what_you_can_expect: '',
      results: '',
      risks: '',
      content: '',
      image: '',
      status: 'active'
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleEdit = async (testProcedure) => {
    try {
      let fullTestProcedure = testProcedure;
      if (!testProcedure.overview && !testProcedure.why_its_done && testProcedure.id) {
        const token = getAccessToken();
        try {
          const response = await axios.get(`/api/tests-procedures/${testProcedure.id}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          fullTestProcedure = response.data;
        } catch (error) {
          console.error('Error fetching full test/procedure data:', error);
        }
      }
      
      setEditingTestProcedure(fullTestProcedure);
      setFormData({
        name: fullTestProcedure.name || '',
        alias: fullTestProcedure.alias || '',
        first_letter: fullTestProcedure.first_letter || '',
        description: fullTestProcedure.description || '',
        overview: fullTestProcedure.overview || '',
        why_its_done: fullTestProcedure.why_its_done || '',
        how_you_prepare: fullTestProcedure.how_you_prepare || '',
        what_you_can_expect: fullTestProcedure.what_you_can_expect || '',
        results: fullTestProcedure.results || '',
        risks: fullTestProcedure.risks || '',
        content: fullTestProcedure.content || '',
        image: fullTestProcedure.image || '',
        status: fullTestProcedure.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error in handleEdit:', error);
      setEditingTestProcedure(testProcedure);
      setFormData({
        name: testProcedure.name || '',
        alias: testProcedure.alias || '',
        first_letter: testProcedure.first_letter || '',
        description: testProcedure.description || '',
        overview: testProcedure.overview || '',
        why_its_done: testProcedure.why_its_done || '',
        how_you_prepare: testProcedure.how_you_prepare || '',
        what_you_can_expect: testProcedure.what_you_can_expect || '',
        results: testProcedure.results || '',
        risks: testProcedure.risks || '',
        content: testProcedure.content || '',
        image: testProcedure.image || '',
        status: testProcedure.status || 'active'
      });
      setFormError('');
      setShowEditModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const token = getAccessToken();
      const data = {
        ...formData,
        first_letter: formData.first_letter || formData.name.charAt(0).toUpperCase()
      };

      if (editingTestProcedure) {
        await axios.put(`/api/tests-procedures/${editingTestProcedure.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Test/Procedure updated successfully!');
      } else {
        await axios.post('/api/tests-procedures', data, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        alert('Test/Procedure created successfully!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingTestProcedure(null);
      await fetchTestsProcedures();
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to save test/procedure';
      setFormError(errorMessage);
      
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        navigate('/admin/login');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test/procedure?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/tests-procedures/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      alert('Test/Procedure deleted successfully!');
      fetchTestsProcedures();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete test/procedure');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 mb-0">Tests & Procedures Management</h1>
          </div>
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleAdd}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Test/Procedure
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Search tests & procedures..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={letterFilter}
              onChange={(e) => setLetterFilter(e.target.value)}
            >
              <option value="">All Letters</option>
              {alphabet.map(letter => (
                <option key={letter} value={letter}>{letter}</option>
              ))}
              <option value="#">#</option>
            </select>
          </div>
        </div>

        {/* Tests & Procedures Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Alias</th>
                    <th>First Letter</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {testsProcedures.map((testProcedure) => (
                    <tr key={testProcedure.id}>
                      <td>{testProcedure.name}</td>
                      <td>{testProcedure.alias || '-'}</td>
                      <td>{testProcedure.first_letter}</td>
                      <td>
                        <span className={`badge bg-${testProcedure.status === 'active' ? 'success' : 'secondary'}`}>
                          {testProcedure.status}
                        </span>
                      </td>
                      <td>{new Date(testProcedure.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => handleEdit(testProcedure)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(testProcedure.id)}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={loading || (pagination.page || 1) <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingTestProcedure ? 'Edit Test/Procedure' : 'Add New Test/Procedure'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setEditingTestProcedure(null);
                    setFormError('');
                  }}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {formError && (
                    <div className="alert alert-danger" role="alert">
                      {formError}
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="alias" className="form-label">
                      Alias (Alternative Name)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="alias"
                      value={formData.alias}
                      onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                      placeholder="e.g., CT scan for Computed Tomography"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="first_letter" className="form-label">
                      First Letter
                    </label>
                    <select
                      className="form-select"
                      id="first_letter"
                      value={formData.first_letter}
                      onChange={(e) => setFormData({ ...formData, first_letter: e.target.value })}
                    >
                      <option value="">Auto (from name)</option>
                      {alphabet.map(letter => (
                        <option key={letter} value={letter}>{letter}</option>
                      ))}
                      <option value="#">#</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description (Short summary)
                    </label>
                    <textarea
                      className="form-control"
                      id="description"
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Image</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <small className="text-muted d-block mt-1">Uploading...</small>}
                    <small className="text-muted d-block mt-1">Image size must be less than 5MB</small>
                    {formData.image && (
                      <div className="mt-2">
                        <img 
                          src={getUploadUrl(formData.image)} 
                          alt="Preview" 
                          style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setFormData({ ...formData, image: '' })}
                          >
                            <i className="bi bi-trash me-1"></i>
                            Remove Image
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Overview"
                      value={formData.overview}
                      onChange={(value) => setFormData({ ...formData, overview: value })}
                      placeholder="Detailed overview. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Why it's done"
                      value={formData.why_its_done}
                      onChange={(value) => setFormData({ ...formData, why_its_done: value })}
                      placeholder="Explain why this test/procedure is performed. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="How you prepare"
                      value={formData.how_you_prepare}
                      onChange={(value) => setFormData({ ...formData, how_you_prepare: value })}
                      placeholder="Instructions for preparation. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="What you can expect"
                      value={formData.what_you_can_expect}
                      onChange={(value) => setFormData({ ...formData, what_you_can_expect: value })}
                      placeholder="What to expect during the test/procedure. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Results"
                      value={formData.results}
                      onChange={(value) => setFormData({ ...formData, results: value })}
                      placeholder="Information about results. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <TextEditor
                      label="Risks"
                      value={formData.risks}
                      onChange={(value) => setFormData({ ...formData, risks: value })}
                      placeholder="Potential risks or complications. Use new lines for paragraphs. Use **text** for bold or # for headings."
                      rows={5}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="content" className="form-label">
                      Additional Content (HTML - Optional)
                    </label>
                    <textarea
                      className="form-control"
                      id="content"
                      rows="4"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Additional content in HTML format (optional)"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select"
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setEditingTestProcedure(null);
                      setFormError('');
                    }}
                    disabled={formLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formLoading}
                  >
                    {formLoading ? 'Saving...' : editingTestProcedure ? 'Update Test/Procedure' : 'Create Test/Procedure'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminTestsProcedures;
